import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { EMPTY, Observable, Subject } from 'rxjs';
import { switchMap, takeUntil, tap } from 'rxjs/operators';

import { AuthService, ConfigService } from '../../../../../../core';
import { IModalSettings } from '../../../../../../domains/shared/components/modal/modal.component';
import { INovoDatepickerValue } from '../../../../../../domains/shared/components/novo-datepicker/novo-datepicker-settings.interface';
import { toLocalDateString } from '../../../../../../domains/shared/components/novo-datepicker/novo-datepicker.utils';
import { ISelectOption } from '../../../../../../domains/shared/components/select/models/select-option.interface';
import { ISelectSettings } from '../../../../../../domains/shared/components/select/models/select-settings.interface';
import { isModoCentralCompraCadastro } from '../../../../../../models/modo-central-compra';
import {
  CidadeSearchesHttpService,
  CidadeSubRegiao,
  CidadeSubRegiaoBairro
} from '../../../../../cidade';
import { RegiaoCidadeSubRegiaoHttpService } from '../../../../../regiao/cidade/sub-regiao/services';
import { EnumUsuarioPerfil } from '../../../../../../domains/shared/components/usuario-multi-select/enums/usuario-perfil.enum';
import { EnumSituacao } from '../../../../../../domains/shared/enums/situacao.enum';
import { ClienteAnaliseFinanceiraAuthGuardService } from '../../../analise-financeira/services';
import { IFiltrosAvancados } from '../../filtros-avancados';

interface IConfig {
  canVisualizarStatusesAnaliseFinanceira: boolean;
  isModoCentralCompraCadastro: boolean;
}

const createDefaultForm = () => ({
  tiposPessoas: [],
  cnpjCpf: '',
  codigoIntegracaoErp: '',
  idClienteGrupoEconomico: null,
  origens: [],
  idsCanaisVendaOrigem: [],
  canaisMidiasSociais: [],
  idsCampanhasOrigens: [],
  idsCampanhasAquisicoes: [],
  idsPeriodicidadesCompras: [],
  periodicidadeCompraSemana: [],
  idsGerentes: [],
  idsAtendentes: [],
  dataCadastroInicial: null,
  dataCadastroFinal: null,
  clienteCentralCompras: null,
  idCidade: null,
  idsRegioes: null,
  idEstado: null,
  idCidadeSubRegiao: null,
  idCidadeSubRegiaoBairro: null
});

const SELECT_DEPENDENTE_VAZIO: Partial<ISelectSettings> = {
  placeholder: 'Selecione',
  isDisabled: true
};

@Component({
  selector: 'sgt-novo-cliente-filter-avancado',
  templateUrl: './novo-cliente-filter-avancado.component.html',
  styleUrls: ['./novo-cliente-filter-avancado.component.scss']
})
export class NovoClienteFilterAvancadoComponent implements OnInit, OnDestroy {
  @Input() isOpen = false;
  @Input() idsRepresentantes: number[] = [];
  @Input() initialValue: Partial<IFiltrosAvancados> | null = null;

  @Output() apply = new EventEmitter<IFiltrosAvancados>();
  @Output() cleared = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  advancedFiltersForm: FormGroup;

  modalSettings: IModalSettings = {
    position: 'right',
    width: '500px',
    hasHeader: true,
    hasFooter: true,
    showCloseButton: true,
    allowEscapeToClose: false
  };

  config: IConfig;

  somenteRegioesAtivas = true;
  statusesAnaliseFinanceira: number[] = [];
  subRegioes: CidadeSubRegiao[] = [];

  readonly EnumUsuarioPerfil = EnumUsuarioPerfil;
  readonly EnumSituacao = EnumSituacao;
  bairros: CidadeSubRegiaoBairro[] = [];

  subRegioesOptions: ISelectOption[] = [];
  bairrosOptions: ISelectOption[] = [];
  subRegiaoSettings: Partial<ISelectSettings> = SELECT_DEPENDENTE_VAZIO;
  bairroSettings: Partial<ISelectSettings> = SELECT_DEPENDENTE_VAZIO;

  private isRepresentante: boolean;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private configService: ConfigService,
    private cidadeSearchesHttpService: CidadeSearchesHttpService,
    private regiaoCidadeSubRegiaoHttpService: RegiaoCidadeSubRegiaoHttpService,
    private clienteAnaliseFinanceiraAuthGuardService: ClienteAnaliseFinanceiraAuthGuardService
  ) {}

  ngOnInit() {
    const configEmpresa = this.configService.getEmpresa();
    this.isRepresentante = this.authService.isRepresentante();

    this.config = {
      canVisualizarStatusesAnaliseFinanceira:
        this.clienteAnaliseFinanceiraAuthGuardService.canVisualizar(),
      isModoCentralCompraCadastro: isModoCentralCompraCadastro(
        configEmpresa?.modoCentralCompra
      )
    };

    this.advancedFiltersForm = this.fb.group(createDefaultForm());
    this.subscribeToCidade();
    this.subscribeToSubRegiao();
    this.patchFormWithInitialValue();
  }

  private patchFormWithInitialValue(): void {
    const value = this.initialValue;
    if (!value) {
return;
}

    const toDatepickerValue = (date: string | null): INovoDatepickerValue | null => {
      if (!date) {
return null;
}
      const dateStr = date.includes('T') ? toLocalDateString(new Date(date)) : date;
      return { date: dateStr, periodicidade: 1 };
    };

    const { cidade, dataCadastroInicial, dataCadastroFinal, statusesAnaliseFinanceira, ...rest } = value;

    this.advancedFiltersForm.patchValue({
      ...rest,
      idCidade: cidade,
      dataCadastroInicial: toDatepickerValue(dataCadastroInicial),
      dataCadastroFinal: toDatepickerValue(dataCadastroFinal)
    }, { emitEvent: false });

    this.statusesAnaliseFinanceira = value.statusesAnaliseFinanceira ?? [];

    if (this.advancedFiltersForm.get('idCidade')?.value) {
      this.loadSubRegioes();
    }
  }

  get filtrosAvancadosValue(): IFiltrosAvancados {
    const { idCidade, dataCadastroInicial, dataCadastroFinal, ...rest } = this.advancedFiltersForm.getRawValue();
    const toDateValue = (v: INovoDatepickerValue | null): string | null =>
      v?.date as string ?? null;
    return {
      ...rest,
      cidade: idCidade ?? null,
      dataCadastroInicial: toDateValue(dataCadastroInicial),
      dataCadastroFinal: toDateValue(dataCadastroFinal),
      statusesAnaliseFinanceira: this.statusesAnaliseFinanceira
    };
  }

  onClose() {
    this.closed.emit();
  }

  onApply() {
    this.apply.emit(this.filtrosAvancadosValue);
    this.closed.emit();
  }

  onClear() {
    this.advancedFiltersForm.reset(createDefaultForm());
    this.statusesAnaliseFinanceira = [];
    this.somenteRegioesAtivas = true;
    this.definirSubRegioes([]);
    this.definirBairros([]);
    this.cleared.emit();
    this.closed.emit();
  }

  private subscribeToCidade() {
    this.advancedFiltersForm
      .get('idCidade')!
      .valueChanges.pipe(
        tap(() => {
          this.advancedFiltersForm.get('idCidadeSubRegiao')!.setValue(null, { emitEvent: false });
          this.advancedFiltersForm.get('idCidadeSubRegiaoBairro')!.setValue(null, { emitEvent: false });
          this.definirSubRegioes([]);
          this.definirBairros([]);
        }),
        switchMap(() => this.buildSubRegiaoRequest$()),
        takeUntil(this.destroy$)
      )
      .subscribe(entities => {
        this.definirSubRegioes(entities);
      });
  }

  private subscribeToSubRegiao() {
    this.advancedFiltersForm
      .get('idCidadeSubRegiao')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((id: number) => {
        this.advancedFiltersForm
          .get('idCidadeSubRegiaoBairro')!
          .setValue(null, { emitEvent: false });
        this.searchCidadeSubRegiaoBairros(id);
      });
  }

  private buildSubRegiaoRequest$(): Observable<CidadeSubRegiao[]> {
    const cidadeRaw = this.advancedFiltersForm.get('idCidade')?.value;
    const idCidade: number = cidadeRaw?.id ?? cidadeRaw;
    if (!idCidade) {
return EMPTY;
}

    const ids: number[] = this.isRepresentante
      ? [this.authService.getIdRepresentante()]
      : this.idsRepresentantes;

    return ids.length > 0
      ? this.regiaoCidadeSubRegiaoHttpService.searchDisponiveisParaRepresentantes(ids, idCidade)
      : this.cidadeSearchesHttpService.searchSubRegioes(idCidade, true);
  }

  private loadSubRegioes() {
    const idCidadeSubRegiaoAtual = this.advancedFiltersForm.get('idCidadeSubRegiao')!.value as number;
    this.buildSubRegiaoRequest$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(entities => {
        this.definirSubRegioes(entities);
        this.searchCidadeSubRegiaoBairros(idCidadeSubRegiaoAtual);
      });
  }

  private searchCidadeSubRegiaoBairros(idCidadeSubRegiao: number) {
    const subRegiao = this.subRegioes.find(sr => sr.id === idCidadeSubRegiao);
    this.definirBairros(subRegiao?.bairros ?? []);
  }

  private definirSubRegioes(entities: CidadeSubRegiao[]): void {
    this.subRegioes = entities;
    this.subRegioesOptions = entities.map(sr => ({ id: sr.id, label: sr.nome }));
    this.subRegiaoSettings = this.advancedFiltersForm?.get('idCidade')?.value
      ? { placeholder: 'Todas as sub-regiões', isClearSelectionAllowed: true }
      : SELECT_DEPENDENTE_VAZIO;
  }

  private definirBairros(entities: CidadeSubRegiaoBairro[]): void {
    this.bairros = entities;
    this.bairrosOptions = entities.map(b => ({ id: b.id, label: b.nome }));
    this.bairroSettings = this.advancedFiltersForm?.get('idCidadeSubRegiao')?.value
      ? { placeholder: 'Todos os bairros', isClearSelectionAllowed: true }
      : SELECT_DEPENDENTE_VAZIO;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
